<template>
  <div v-if="showResultsDuringTheLoading" class="c-global-search-results overflow-auto">
    <div
      v-if="transformedResults.length === 0"
      class="c-global-search-results__no-results text-center items-center p-4 flex flex-col gap-2 border-rounded-1"
    >
      <CIcon class="text-size-9" fa-type="fal" icon="radar" />
      {{ t('canvasUI.CGlobalSearch.noResults') }}
    </div>

    <div v-else class="flex flex-col gap-4">
      <CResultSection
        v-for="section in sections"
        :key="section.icon"
        :icon="section.icon"
        :quantity="section.quantity"
        :scope="section.scope"
        :title="section.title"
        :type="section.type"
      >
        <CResult
          v-for="item in groupedResults[section.type]"
          :key="item.id"
          :data-search-result-id="item.id"
          :icon="section.icon"
          :item="item"
          :root-folder-id="rootFolderId"
          :selected="selectedResult === item"
          @focus="selectElement(item)"
          @mouseenter="selectElement(item)"
          @open="onOpen"
          @open-folder="onOpenFolder"
        />
      </CResultSection>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { ComputedRef, Ref } from 'vue'
import { computed, ref, watch, toRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { groupBy } from 'lodash-es'

import { useShortcuts } from '@lib/components/CGlobalSearch/Shortcuts.use'
import { GlobalSearchResultType, type GlobalSearchResult } from '@lib/components/CGlobalSearch/CGlobalSearch.types'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CResult from '@lib/components/CGlobalSearch/CResult.vue'
import CResultSection from '@lib/components/CGlobalSearch/CResultSection.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import type { Folder } from '@openapi'

type SearchResultByGroup = {
  [key in GlobalSearchResultType]: GlobalSearchResult[]
}

interface SearchResultSection {
  type: GlobalSearchResultType
  icon: string
  quantity: number
  scope: string
  title: string
}

interface Props {
  rootFolderId?: string
  results?: GlobalSearchResult[]
  isLoading: boolean
  organizationName: string
  instanceName: string
  search: string
  searchRef: HTMLElement | null
}

const props = defineProps<Props>()
const emit = defineEmits<{
  toggleModal: [isOpen: boolean]
  open: [item: GlobalSearchResult]
  openFolder: [folderId: Folder['id']]
}>()

const GROUP_SEQUENCE = [
  GlobalSearchResultType.File,
  GlobalSearchResultType.User,
  GlobalSearchResultType.Group,
  GlobalSearchResultType.App,
]

const trimmedSearch = computed(() => props.search.trim())
const { t } = useI18n()
const themeVars = useThemeVars()

const showResultsDuringTheLoading = ref(!props.isLoading)
const searchRef = toRef(props, 'searchRef')

// Cache results; it waits until loading of the new results will finish and prevents sections from blinking
const cachedResultsDuringLoading: Ref<GlobalSearchResult[]> = ref([])
const transformedResults: ComputedRef<GlobalSearchResult[]> = computed(() =>
  props.isLoading ? cachedResultsDuringLoading.value : (props.results ?? []),
)

const groupedResults: ComputedRef<SearchResultByGroup> = computed(
  () => groupBy(transformedResults.value, 'type') as SearchResultByGroup,
)

const resultsOrderedByGroup: ComputedRef<GlobalSearchResult[]> = computed(() =>
  GROUP_SEQUENCE.reduce((acc: GlobalSearchResult[], type) => [...acc, ...(groupedResults.value[type] ?? [])], []).sort(
    (a, b) => GROUP_SEQUENCE.indexOf(a.type) - GROUP_SEQUENCE.indexOf(b.type),
  ),
)

const closeModal = () => emit('toggleModal', false)

const onOpen = (item: GlobalSearchResult) => {
  emit('open', item)
  closeModal()
}
const onOpenFolder = (folderId: string) => {
  emit('openFolder', folderId)
  closeModal()
}
const { selectElement, selectedResult } = useShortcuts(resultsOrderedByGroup, searchRef)

const sections: ComputedRef<SearchResultSection[]> = computed(() =>
  [
    {
      type: GlobalSearchResultType.File,
      icon: 'folder',
      quantity: groupedResults.value?.file?.length ?? 0,
      scope: props.instanceName,
      title: t('canvasUI.CGlobalSearch.sections.files'),
    },
    {
      type: GlobalSearchResultType.User,
      icon: 'user',
      quantity: groupedResults.value?.user?.length ?? 0,
      scope: props.instanceName,
      title: t('canvasUI.CGlobalSearch.sections.users'),
    },
    {
      type: GlobalSearchResultType.Group,
      icon: 'users',
      quantity: groupedResults.value?.group?.length ?? 0,
      scope: props.organizationName,
      title: t('canvasUI.CGlobalSearch.sections.groups'),
    },
    {
      type: GlobalSearchResultType.App,
      icon: 'rocket',
      quantity: groupedResults.value?.app?.length ?? 0,
      scope: props.instanceName,
      title: t('canvasUI.CGlobalSearch.sections.apps'),
    },
  ]
    .filter((s) => s.quantity > 0)
    .sort((a, b) => GROUP_SEQUENCE.indexOf(a.type) - GROUP_SEQUENCE.indexOf(b.type)),
)

watch(
  () => props.isLoading,
  () => {
    if (props.isLoading && props.results) {
      cachedResultsDuringLoading.value = props.results
    }

    if (!props.isLoading) {
      showResultsDuringTheLoading.value = true
    }
  },
)

watch(
  trimmedSearch,
  () => {
    if (trimmedSearch.value === '') {
      showResultsDuringTheLoading.value = false
    }
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
.c-global-search-results {
  &__no-results {
    color: v-bind('themeVars.text3');
    background: v-bind('themeVars.primary6');
  }
}
</style>
